import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import PropTypes from "prop-types";
import { Button, Select, SelectItem, TextInput } from "@tremor/react";
import { TitlePageInfo } from "../components";
import { adminAPI } from "../../api/adminAPI";
import { changeJWT } from "../../store/auth";
import { AlertBox } from "../../ui";

export const FormUserAdminLayout = ({ isEditMode = false, titlePage = "" }) => {
  const { jwt } = useSelector((state) => state.auth);
  const [error, setError] = useState({
    show: false,
    message: "",
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [profiles, setProfiles] = useState([]);
  const { id } = useParams();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    setValue,
  } = useForm();

  useState(async () => {
    const { data } = await adminAPI.get("/profile", {
      headers: {
        "x-token": jwt,
      },
    });
    const info = data.data.map(({ name, id }) => {
      return {
        name,
        id,
      };
    });
    setProfiles(info);

    if (isEditMode) {
      const { data, status } = await adminAPI
        .get(`/admin/${id}`, {
          headers: {
            "x-token": jwt,
          },
        })
        .catch(({ response }) => response);

      if (status !== 200) {
        setError({
          show: true,
          message: data.message,
        });
        return AlertBox({ color: "error", messages: [data.message] });
      }

      setValue("name", `${data.data.name} ${data.data.lastName}`);
      setValue("email", data.data.email);
      setValue("phone", data.data.phone);
      setValue("profileId", data.data.profileId);
    }
  }, []);
  const onSubmit = async ({
    name,
    email,
    phone,
    password,
    cpassword,
    profileId,
  }) => {
    const lastName = name.split(" ")[1] ?? "";
    const realName = name.split(" ")[0] ?? "";
    const data = {
      name: realName,
      lastName,
      email,
      phone,
      password,
      cpassword,
      profileId,
    };
    if (isEditMode) {
      const response = await adminAPI
        .patch(`/admin/${id}`, data, {
          headers: { "x-token": jwt },
        })
        .catch(({ response }) => response);
      if (response.status !== 200) {
        const { data } = response;
        const messages = data.message
          ? [data.message]
          : data.errors.map((err) => err.msg);
        AlertBox({ color: "error", messages });
      }
      AlertBox({ color: "success", messages: [response.data.message] });
      dispatch(changeJWT(response.data.data));
      navigate("/admin/");
      return;
    }
    const response = await adminAPI
      .post("/auth/sign-up", data, {
        headers: { "x-token": jwt },
      })
      .catch(({ response }) => response);
    if (response.status !== 200) {
      const { data } = response;
      const errors = data.message
        ? [data.message]
        : data.errors.map((err) => err.msg);
      AlertBox({ color: "warning", messages: errors });
      return;
    }
    AlertBox({
      color: "success",
      messages: ["Administrador Creado con Exito."],
    });
    navigate("/admin/");
  };

  return (
    <>
      <TitlePageInfo title={titlePage} />

      {error.show && (
        <>
          <div className="overflow-hidden">
            <div className="flex items-center justify-center m-auto h-screen w-full  animate__animated animate__zoomIn">
              <h1 className="text-2xl text-black font-bold">{error.message}</h1>
            </div>
          </div>
        </>
      )}
      {!error.show && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div>
            <p>Nombre</p>
            <TextInput
              type="text"
              className="w-full "
              placeholder="Juan Pérez"
              name="name"
              {...register("name", {
                required: {
                  value: true,
                  message: "Nombre es requerido",
                },
                minLength: {
                  value: 5,
                  message: "Nombre debe tener al menos 5 caracteres",
                },
                maxLength: {
                  value: 20,
                  message: "Nombre debe tener menos de 20 caracteres",
                },
                pattern: {
                  value: /^[a-zA-Z ]+$/,
                  message: "Nombre no es valido",
                },
              })}
            />
            {errors.name && (
              <p className="text-red-500 text-xs italic">
                {errors.name.message}
              </p>
            )}
          </div>
          <div>
            <p>Correo Electrónico</p>
            <TextInput
              type="text"
              className="w-full "
              placeholder="email@email.com"
              name="email"
              {...register("email", {
                required: {
                  value: true,
                  message: "Email es requerido",
                },
                isEmail: {
                  value: true,
                  message: "Email no es valido",
                },
                maxLength: {
                  value: 50,
                  message: "Email debe tener menos de 50 caracteres",
                },
                minLength: {
                  value: 5,
                  message: "Email debe tener al menos 5 caracteres",
                },
                pattern: {
                  value: /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/,
                  message: "Email no es valido",
                },
              })}
            />
            {errors.email && (
              <p className="text-red-500 text-xs italic">
                {errors.email.message}
              </p>
            )}
          </div>
          <div>
            <p>Teléfono</p>
            <TextInput
              type="tel"
              className="w-full "
              placeholder="81 1000 0000"
              name="phone"
              {...register("phone", {
                required: {
                  value: true,
                  message: "Teléfono es requerido",
                },
                maxLength: {
                  value: 10,
                  message: "Teléfono debe tener menos de 10 caracteres",
                },
                minLength: {
                  value: 10,
                  message: "Teléfono debe tener al menos 10 caracteres",
                },
                pattern: {
                  value: /^[0-9]{10}$/,
                  message: "Teléfono no es valido, debe ser solo 10  dígitos",
                },
              })}
            />
            {errors.phone && (
              <p className="text-red-500 text-xs italic">
                {errors.phone.message}
              </p>
            )}
          </div>
          <div>
            <p>Perfil de Usuario</p>
            <Controller
              name="profileId"
              rules={{ required: "Perfil es requerido" }}
              control={control}
              render={({ field }) => (
                <>
                  <Select
                    name="profileId"
                    placeholder="Seleccionar..."
                    {...field}
                  >
                    {profiles.length > 0 ? (
                      profiles.map((profile) => (
                        <SelectItem key={profile.id} value={profile.id}>
                          {profile.name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem>No Profiles</SelectItem>
                    )}
                  </Select>
                  {errors.profileId && (
                    <p className="text-red-500 text-xs italic">
                      {errors.profileId.message}
                    </p>
                  )}
                </>
              )}
            />
          </div>
          <div className="grid grid-cols-2 gap-2 ">
            <div>
              <p>Contraseña</p>
              <TextInput
                type="password"
                className="w-full "
                placeholder="Pass1234"
                name="password"
                {...register(
                  "password",
                  isEditMode
                    ? {}
                    : {
                        required: {
                          value: true,
                          message: "Contraseña es requerido",
                        },
                        maxLength: {
                          value: 20,
                          message:
                            "Contraseña debe tener menos de 20 caracteres",
                        },
                        minLength: {
                          value: 6,
                          message:
                            "Contraseña debe tener al menos 6 caracteres",
                        },
                      }
                )}
              />
              {errors.password && (
                <p className="text-red-500 text-xs italic">
                  {errors.password.message}
                </p>
              )}
            </div>
            <div>
              <p>Confirmar Contraseña</p>
              <TextInput
                type="password"
                className="w-full "
                name="cpassword"
                placeholder="Pass1234"
                {...register(
                  "cpassword",
                  isEditMode
                    ? {}
                    : {
                        required: {
                          value: true,
                          message: "Confirmar Contraseña es requerido",
                        },
                        maxLength: {
                          value: 20,
                          message:
                            "Confirmar Contraseña debe tener menos de 20 caracteres",
                        },
                        minLength: {
                          value: 6,
                          message:
                            "Confirmar Contraseña debe tener al menos 6 caracteres",
                        },
                      }
                )}
              />
              {errors.cpassword && (
                <p className="text-red-500 text-xs italic">
                  {errors.cpassword.message}
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-end mt-2">
            {isEditMode ? (
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Editando..." : "Editar"}
              </Button>
            ) : (
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Guardando..." : "Guardar"}
              </Button>
            )}
          </div>
        </form>
      )}
    </>
  );
};

FormUserAdminLayout.propTypes = {
  isEditMode: PropTypes.bool,
  titlePage: PropTypes.string,
};
